// Calls in the browser (docs/web-konzept.md stage 3), following the app's CallController
// (app/.../call/CallController.kt): the routes under /v1/calls and /v1/conversations/{id}/calls,
// the {"t":"call"} frames, LiveKit for the media. One call at a time; a second one that rings
// while this one is busy is declined as "busy", or offered as call waiting while talking.

import { useSyncExternalStore } from "react";
import { ApiError } from "../api/client.ts";
import type { CallResponse } from "../api/contract.gen.ts";
import type { Session } from "../auth/session.ts";
import { LiveTranscript } from "../audio/liveTranscript.ts";
import { VOXIE_ID } from "../data/display.ts";
import type { ServerFrame } from "../realtime/realtime.ts";
import { type AgentLine, CallRoom, type Peer } from "./room.ts";
import { Tone } from "./tones.ts";

export type Phase = "outgoing" | "incoming" | "connecting" | "active" | "reconnecting" | "ended";

interface JoinAnswer {
  call: CallResponse;
  livekit: { url: string; room: string; token: string };
}

export interface CaptionLine {
  speaker: string;
  order: number;
  text: string;
  final: boolean;
  translated?: string;
}

export interface CallState {
  phase: Phase;
  callId: string;
  conversationId: string;
  kind: "voice" | "video";
  /** The other side (1:1), the caller (incoming), or the group. */
  from: string;
  voxie: boolean;
  outgoing: boolean;
  delivered: boolean;
  ringReason?: string;
  endReason?: string;
  connectedAt?: number;
  micOn: boolean;
  cameraOn: boolean;
  screenOn: boolean;
  peers: Peer[];
  me?: Peer;
  captionsOn: boolean;
  captionLanguage: string | null;
  captions: CaptionLine[];
  translateFailed: boolean;
  /** I transcribe my microphone for the others (somebody asked for captions). */
  transcribing: boolean;
  agentLines: AgentLine[];
  reactions: { from: string; emoji: string; at: number }[];
  hands: string[];
  videoRequestFrom?: string;
  /** A second call ringing while this one is active (call waiting). */
  waiting?: { callId: string; from: string; kind: string };
  error?: string;
}

const RING_MS = 45_000;
const OUTGOING_MS = RING_MS + 5_000;
const CONNECT_MS = 20_000;
const RECONNECT_MS = 30_000;
const ENDED_MS = 2_500;
const ENDED_UNANSWERED_MS = 60_000;
export const REACTIONS = ["👍", "❤️", "😂", "😮", "👏", "🎉"];

const ERRORS: Record<string, string> = {
  CALL_BUSY: "Besetzt",
  CALL_PAUSED: "Gerade nicht anrufbar. Schick lieber eine Sprachnachricht.",
  CALL_NOT_ALLOWED: "Diese Person kann nicht angerufen werden.",
  CALL_UNAVAILABLE: "Anrufe sind auf diesem Server nicht verfügbar.",
  CALL_ENDED: "Anruf beendet",
  CALL_ANSWERED: "Anruf beendet",
  RATE_LIMITED: "Zu viele Anrufe hintereinander. Bitte kurz warten.",
};

export function errorText(e: unknown): string {
  return e instanceof ApiError ? (ERRORS[e.code] ?? "Anruf nicht möglich") : "Anruf nicht möglich";
}

export class CallController {
  private state: CallState | null = null;
  /** Running calls by conversation (for "Gruppenanruf läuft · Beitreten"). */
  readonly running = new Map<string, CallResponse>();
  private room: CallRoom | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly tone = new Tone();
  private readonly listeners = new Set<() => void>();
  private captionAskers = new Set<string>();
  private transcript: LiveTranscript | null = null;
  private transcriptAudio: AudioContext | null = null;
  private translating = new Map<string, boolean>();
  private readonly session: Session;
  private readonly me: () => string | null;
  private readonly notify: (title: string, body: string, conversationId: string) => void;

  constructor(session: Session, me: () => string | null, notify: (title: string, body: string, conversationId: string) => void) {
    this.session = session;
    this.me = me;
    this.notify = notify;
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getState = () => this.state;

  // ---- starting, answering, ending ----

  async start(conversationId: string, kind: "voice" | "video", voxie = false): Promise<void> {
    if (this.state && this.state.phase !== "ended") return;
    this.set({ ...this.blank(conversationId, kind, voxie ? VOXIE_ID : "", voxie), phase: voxie ? "connecting" : "outgoing", outgoing: true });
    try {
      const answer = await this.session.call<JoinAnswer>(`/v1/conversations/${conversationId}/calls`, { method: "POST", json: { kind } });
      this.patch({ callId: answer.call.id });
      const direct = answer.call.state === "active" || answer.call.participants.some((p) => p.user_id === this.me() && p.state === "joined" && answer.call.started_by !== this.me());
      if (!voxie && !direct) {
        this.tone.ringback();
        this.after(OUTGOING_MS, () => void this.hangup("no_answer"));
      }
      await this.connect(answer, kind === "video");
    } catch (e) {
      this.end(e instanceof ApiError && e.code === "CALL_BUSY" ? "busy" : "failed", errorText(e));
    }
  }

  /** Joins a group call that runs without us. */
  async join(conversationId: string): Promise<void> {
    const running = this.running.get(conversationId);
    await this.start(conversationId, (running?.kind as "voice" | "video") ?? "voice");
  }

  async accept(withVideo: boolean): Promise<void> {
    const s = this.state;
    if (!s || s.phase !== "incoming") return;
    this.tone.stop();
    this.patch({ phase: "connecting" });
    try {
      const answer = await this.session.call<JoinAnswer>(`/v1/calls/${s.callId}/accept`, { method: "POST", json: {} });
      await this.connect(answer, withVideo);
    } catch (e) {
      this.end(e instanceof ApiError && e.code === "CALL_ANSWERED" ? "answered_elsewhere" : "failed", errorText(e));
    }
  }

  async decline(reason: "declined" | "busy" = "declined", callId = this.state?.callId): Promise<void> {
    if (!callId) return;
    void this.session.call(`/v1/calls/${callId}/decline`, { method: "POST", json: { reason } }).catch(() => undefined);
    if (callId === this.state?.callId) this.end("declined");
  }

  async hangup(reason = "hangup"): Promise<void> {
    const s = this.state;
    if (!s || s.phase === "ended") return;
    if (s.callId) void this.session.call(`/v1/calls/${s.callId}/hangup`, { method: "POST", json: {} }).catch(() => undefined);
    this.end(reason);
  }

  /** "Auflegen und annehmen": the waiting call instead of this one (the server ends this one). */
  async switchToWaiting(): Promise<void> {
    const w = this.state?.waiting;
    if (!w) return;
    await this.leaveRoom();
    this.set({ ...this.blank("", w.kind as "voice" | "video", w.from, false), callId: w.callId, phase: "incoming" });
    const call = await this.session.call<CallResponse>(`/v1/calls/${w.callId}`).catch(() => null);
    if (call) this.patch({ conversationId: call.conversation_id });
    await this.accept(false);
  }

  dismissWaiting() {
    const w = this.state?.waiting;
    if (w) void this.decline("declined", w.callId);
    this.patch({ waiting: undefined });
  }

  close() {
    if (this.state?.phase === "ended") this.set(null);
  }

  // ---- in the call ----

  async toggleMic() {
    if (!this.room) return;
    await this.room.setMic(!this.room.micOn);
    this.refresh();
  }

  async toggleCamera() {
    const r = this.room;
    if (!r) return;
    const on = !r.cameraOn;
    await r.setCamera(on).catch(() => this.patch({ error: "Die Kamera ließ sich nicht starten." }));
    // Picture during an answered voice call: the other side is asked to switch to video too.
    if (this.state?.phase === "active") r.send(on ? "video_request" : "video_withdrawn");
    this.refresh();
  }

  async toggleScreen() {
    if (!this.room) return;
    await this.room.setScreen(!this.room.screenOn).catch(() => undefined);
    this.refresh();
  }

  react(emoji: string) {
    if (!REACTIONS.includes(emoji) || !this.room) return;
    this.room.send(`reaction:${emoji}`);
    this.addReaction(this.me() ?? "", emoji);
  }

  toggleHand() {
    const me = this.me() ?? "";
    const up = !this.state?.hands.includes(me);
    this.room?.send(up ? "hand:up" : "hand:down");
    this.patch({ hands: up ? [...(this.state?.hands ?? []), me] : (this.state?.hands ?? []).filter((h) => h !== me) });
  }

  answerVideoRequest(yes: boolean) {
    this.patch({ videoRequestFrom: undefined });
    if (yes && !this.room?.cameraOn) void this.toggleCamera();
  }

  /** Captions of the others: they transcribe their microphones once someone asks (captions:on). */
  toggleCaptions() {
    const on = !this.state?.captionsOn;
    this.room?.send(on ? "captions:on" : "captions:off");
    this.patch({ captionsOn: on, captions: on ? (this.state?.captions ?? []) : [] });
  }

  setCaptionLanguage(language: string | null) {
    try {
      if (language) localStorage.setItem("voxie.captionLanguage", language);
      else localStorage.removeItem("voxie.captionLanguage");
    } catch {
      // Only for this call then.
    }
    this.patch({ captionLanguage: language, captions: (this.state?.captions ?? []).map((c) => ({ ...c, translated: undefined })), translateFailed: false });
    for (const c of this.state?.captions ?? []) this.translate(c);
  }

  stopVoxie() {
    this.room?.send("voxie:stop");
  }

  // ---- signals ----

  onFrame(frame: ServerFrame) {
    if (frame.t !== "call") return;
    const callId = frame.call as string;
    const conversationId = frame.conversation as string;
    const state = frame.state as string;
    const s = this.state;
    if (state === "ringing" || state === "active") {
      const known = this.running.get(conversationId);
      this.running.set(conversationId, { ...(known ?? ({} as CallResponse)), id: callId, conversation_id: conversationId, kind: frame.kind as string, state, started_by: frame.from as string } as CallResponse);
    }
    if (state === "ended") this.running.delete(conversationId);
    switch (state) {
      case "ringing":
        if (s?.callId === callId || frame.from === this.me()) break;
        void this.incoming(callId);
        break;
      case "active":
        if (s?.callId === callId && s.phase === "incoming" && (!frame.by || frame.by === this.me())) this.end("answered_elsewhere");
        if (s?.callId === callId && s.outgoing && frame.by && frame.by !== this.me()) this.tone.stop();
        break;
      case "ended":
        if (s?.callId === callId && s.phase !== "ended") this.end((frame.reason as string) ?? "hangup");
        if (s?.waiting?.callId === callId) this.patch({ waiting: undefined });
        break;
      case "delivered":
        if (s?.callId === callId) this.patch({ delivered: true });
        break;
      case "reason":
        if (s?.callId === callId) this.patch({ ringReason: frame.reason as string });
        break;
    }
    this.emit();
  }

  /** active_calls of the last sync page: what runs now (no ringing from it). */
  onRunning(calls: CallResponse[]) {
    this.running.clear();
    for (const c of calls) this.running.set(c.conversation_id, c);
    this.emit();
  }

  private async incoming(callId: string) {
    const call = await this.session.call<CallResponse>(`/v1/calls/${callId}`).catch(() => null);
    if (!call || call.state === "ended" || call.started_by === this.me()) return;
    if (!call.participants.some((p) => p.user_id === this.me() && p.state === "ringing")) return;
    const s = this.state;
    if (s && s.phase !== "ended") {
      if (s.phase === "active" || s.phase === "reconnecting") {
        this.patch({ waiting: { callId, from: call.started_by, kind: call.kind } });
        void this.session.call(`/v1/calls/${callId}/ringing`, { method: "POST", json: {} }).catch(() => undefined);
        setTimeout(() => this.state?.waiting?.callId === callId && this.patch({ waiting: undefined }), RING_MS);
      } else {
        void this.decline("busy", callId);
      }
      return;
    }
    this.set({ ...this.blank(call.conversation_id, call.kind as "voice" | "video", call.started_by, call.started_by === VOXIE_ID), callId, phase: "incoming", ringReason: call.ring_reason ?? undefined });
    this.tone.ring();
    void this.session.call(`/v1/calls/${callId}/ringing`, { method: "POST", json: {} }).catch(() => undefined);
    this.after(RING_MS, () => this.end("no_answer"));
    if (document.visibilityState !== "visible") this.notify(call.kind === "video" ? "Videoanruf" : "Sprachanruf", "Voxie öffnen, um anzunehmen", call.conversation_id);
  }

  // ---- the room ----

  private async connect(answer: JoinAnswer, camera: boolean) {
    const s = this.state!;
    const room = new CallRoom({
      onChange: () => this.refresh(),
      onMessage: (from, text) => this.onMessage(from, text),
      onAgentLine: (line) => this.onAgentLine(line),
      onReconnecting: (yes) => {
        this.patch({ phase: yes ? "reconnecting" : "active" });
        if (yes) this.after(RECONNECT_MS, () => this.end("dropped"));
        else this.clearTimer();
      },
      onDisconnected: () => this.state?.phase !== "ended" && this.end(this.state?.voxie ? "hangup" : "dropped"),
      onPeerJoined: (identity) => {
        this.connected();
        if (this.state?.captionsOn) this.room?.send("captions:on", [identity]);
      },
      onPeerLeft: () => {
        if (this.state?.voxie && this.room?.remoteCount === 0) void this.hangup();
      },
    });
    this.room = room;
    if (!s.outgoing || s.voxie) this.after(CONNECT_MS, () => this.end(s.voxie ? "voxie_unavailable" : "dropped"));
    await room.connect(answer.livekit.url, answer.livekit.token, camera);
    if (room.remoteCount > 0) this.connected();
    this.refresh();
  }

  private connected() {
    const s = this.state;
    if (!s || s.phase === "active" || s.phase === "ended") return;
    this.tone.stop();
    this.clearTimer();
    this.patch({ phase: "active", connectedAt: s.connectedAt ?? Date.now() });
  }

  private onMessage(from: string, text: string) {
    const s = this.state;
    if (!s) return;
    if (text === "video_request") this.patch({ videoRequestFrom: this.room?.cameraOn ? undefined : from });
    else if (text === "video_withdrawn") this.patch({ videoRequestFrom: undefined });
    else if (text.startsWith("reaction:")) this.addReaction(from, text.slice(9));
    else if (text === "hand:up") this.patch({ hands: [...s.hands.filter((h) => h !== from), from] });
    else if (text === "hand:down") this.patch({ hands: s.hands.filter((h) => h !== from) });
    else if (text === "captions:on" || text === "captions:off") {
      if (text === "captions:on") this.captionAskers.add(from);
      else this.captionAskers.delete(from);
      void this.updateTranscription();
    } else if (text.startsWith("cap:")) {
      try {
        const c = JSON.parse(text.slice(4)) as { o: number; f: boolean; t: string };
        const line: CaptionLine = { speaker: from, order: c.o, final: c.f, text: c.t.slice(0, 500) };
        const rest = s.captions.filter((x) => !(x.speaker === from && x.order === c.o));
        this.patch({ captions: [...rest, line].slice(-12) });
        this.translate(line);
      } catch {
        // Not a caption line.
      }
    }
  }

  private onAgentLine(line: AgentLine) {
    const s = this.state;
    if (!s) return;
    this.patch({ agentLines: [...s.agentLines.filter((l) => l.id !== line.id), line].slice(-6) });
  }

  private addReaction(from: string, emoji: string) {
    if (!REACTIONS.includes(emoji)) return;
    const at = Date.now();
    this.patch({ reactions: [...(this.state?.reactions ?? []).filter((r) => at - r.at < 4000), { from, emoji, at }] });
    setTimeout(() => this.patch({ reactions: (this.state?.reactions ?? []).filter((r) => Date.now() - r.at < 4000) }), 4100);
  }

  /** Transcribes my microphone while somebody wants captions (app: captionsLocked). */
  private async updateTranscription() {
    const want = this.captionAskers.size > 0 && this.state?.phase === "active";
    if (want && !this.transcript && this.room) {
      const track = this.room.micTrack();
      if (!track) return;
      const lt = new LiveTranscript(this.session);
      this.transcript = lt;
      const ctx = new AudioContext({ sampleRate: 48_000 });
      this.transcriptAudio = ctx;
      await ctx.audioWorklet.addModule("/recorder-worklet.js");
      const node = new AudioWorkletNode(ctx, "voxie-recorder");
      node.port.onmessage = (e: MessageEvent<Float32Array>) => lt.feed(e.data);
      ctx.createMediaStreamSource(new MediaStream([track])).connect(node);
      let last = "";
      lt.onChange = (turn) => {
        if (!turn) return;
        const payload = JSON.stringify({ o: turn.order, f: turn.final, t: turn.text });
        if (payload === last) return;
        last = payload;
        this.room?.send(`cap:${payload}`);
      };
      void lt.start();
      this.patch({ transcribing: true });
    } else if (!want && this.transcript) {
      this.transcript.end();
      this.transcript = null;
      void this.transcriptAudio?.close().catch(() => undefined);
      this.transcriptAudio = null;
      this.patch({ transcribing: false });
    }
  }

  /** One request per speaker at a time; only the newest waiting line goes next (as the app). */
  private translate(line: CaptionLine) {
    const s = this.state;
    if (!s?.captionLanguage || !s.callId) return;
    const key = line.speaker;
    if (this.translating.get(key)) return;
    this.translating.set(key, true);
    void this.session
      .call<{ text: string }>(`/v1/calls/${s.callId}/translate`, { method: "POST", json: { text: line.text, language: s.captionLanguage } })
      .then(
        (r) => {
          const current = this.state;
          if (!current) return;
          this.patch({ translateFailed: false, captions: current.captions.map((c) => (c.speaker === line.speaker && c.order === line.order && c.text === line.text ? { ...c, translated: r.text } : c)) });
        },
        () => this.patch({ translateFailed: true }),
      )
      .finally(() => {
        this.translating.set(key, false);
        const newest = this.state?.captions.filter((c) => c.speaker === key).at(-1);
        if (newest && newest.translated === undefined && newest !== line) setTimeout(() => this.translate(newest), 800);
      });
  }

  // ---- bookkeeping ----

  private end(reason: string, error?: string) {
    const s = this.state;
    if (!s || s.phase === "ended") return;
    this.tone.stop();
    this.clearTimer();
    void this.leaveRoom();
    const unanswered = s.outgoing && ["no_answer", "declined", "busy"].includes(reason);
    this.patch({ phase: "ended", endReason: reason, error, waiting: undefined });
    this.after(error || unanswered ? ENDED_UNANSWERED_MS : ENDED_MS, () => this.close());
  }

  private async leaveRoom() {
    this.captionAskers.clear();
    await this.updateTranscription();
    const room = this.room;
    this.room = null;
    await room?.disconnect().catch(() => undefined);
  }

  private refresh() {
    const r = this.room;
    if (!r || !this.state) return this.emit();
    this.patch({ peers: r.peers(), me: r.me(), micOn: r.micOn, cameraOn: r.cameraOn, screenOn: r.screenOn });
  }

  private blank(conversationId: string, kind: "voice" | "video", from: string, voxie: boolean): CallState {
    let language: string | null = null;
    try {
      language = localStorage.getItem("voxie.captionLanguage");
    } catch {
      // None.
    }
    return {
      phase: "connecting",
      callId: "",
      conversationId,
      kind,
      from,
      voxie,
      outgoing: false,
      delivered: false,
      micOn: true,
      cameraOn: false,
      screenOn: false,
      peers: [],
      captionsOn: false,
      captionLanguage: language,
      captions: [],
      translateFailed: false,
      transcribing: false,
      agentLines: [],
      reactions: [],
      hands: [],
    };
  }

  private after(ms: number, f: () => void) {
    this.clearTimer();
    this.timer = setTimeout(f, ms);
  }

  private clearTimer() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  private set(state: CallState | null) {
    this.state = state;
    this.emit();
  }

  private patch(patch: Partial<CallState>) {
    if (!this.state) return;
    this.state = { ...this.state, ...patch };
    this.emit();
  }

  private emit() {
    for (const l of this.listeners) l();
  }
}

export function useCall(controller: CallController): CallState | null {
  return useSyncExternalStore(controller.subscribe, controller.getState);
}
